/**
 * Soulumo Foundations · CARE FOR SOMEONE ELSE
 *
 * Mar 2026 · Michael's Care-Package direction.
 *
 * Simple, loving tools for being with another human being —
 * without having to fix them. The brief's seven pieces:
 *
 *   1. LISTEN → UNDERSTAND → LOVE  (compass)
 *   2. Before I Help          (new 80 s audio)
 *   3. I Don't Know What to Say  (five phrases)
 *   4. Listen or Help?        (one principle)
 *   5. After I've Been There for Someone  (new 2 min audio)
 *   6. Loving Boundaries      (small teaching + links to Rest / Peace)
 *   7. When Love Means Getting More Help  (safety signpost)
 *
 *   + Your Care Circle (one person · one place · one thing,
 *                       stored privately on user doc)
 *
 * All copy is static (comes from the brief verbatim). The two audio
 * cards link to the FoundationsPlayer when `audio_status === "ready"`
 * and show a quiet "coming today" state otherwise, so launch day is
 * never blocked by audio delivery.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import SoulumoWord from "../../components/SoulumoWord";
import { TOK, SERIF, SANS } from "./foundationsTokens";

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem("reclaim_token");
const auth = () => {
  const t = getToken();
  return t ? { headers: { Authorization: `Bearer ${t}` } } : {};
};

const COMPASS = {
  title: "Listen · Understand · Love",
  intro:
    "You don't always need the right words. Sometimes someone simply needs you to be there.",
  steps: [
    {
      word: "LISTEN",
      body:
        'Don\'t immediately fix. Don\'t diagnose. Don\'t rush to give advice. Create enough space for the other person to tell you what is really happening. A simple invitation: "I\'m here. Tell me what\'s going on."',
    },
    {
      word: "UNDERSTAND",
      body:
        'Help the person feel seen. Not "I know exactly how you feel." Not "This happened for a reason." Instead: "I can understand why that might feel difficult." · "You don\'t have to explain it perfectly." · "I\'m here with you."',
    },
    {
      word: "LOVE",
      body:
        'You don\'t have to remove the pain. Bring connection. Warmth. Presence. A hand held, where welcomed. A breath together. A reminder: "You don\'t have to carry this alone."',
    },
  ],
  anchor: "You don't need to take someone's pain away to love them through it.",
};

const PHRASES_WHEN_STUCK = [
  "I'm here.",
  "You don't have to go through this moment alone.",
  "Would you like me to listen, or would you like help thinking about what to do?",
  "You don't have to explain it perfectly.",
  "What would feel most supportive right now?",
];

const LISTEN_OR_HELP = {
  principle: "Ask before solving.",
  body:
    'When someone shares something difficult, you can ask: "Would you like me just to listen, or would you like us to think about what might help?" It gives them agency — and it removes enormous pressure from you.',
};

const LOVING_BOUNDARIES = {
  anchor: "I can care deeply without carrying everything.",
  lines: [
    "You are allowed to rest.",
    "You are allowed to say no.",
    "You are allowed to take a break.",
    "You are allowed to ask somebody else for help.",
    "You are allowed to have needs of your own.",
    "You are allowed to enjoy your own life.",
    "You can love somebody without being available every second.",
  ],
};

const WHEN_MORE_HELP = {
  heading: "Sometimes being there means bringing someone else in.",
  body:
    "If someone appears to be in immediate danger, is talking about harming themselves or someone else, has a serious medical problem, or the situation feels beyond what you can safely hold — please seek the appropriate emergency, medical or mental-health support. You are not expected to handle everything alone. Sometimes the most caring thing we can do is help someone reach the support they need.",
};

const isReady = (it) =>
  it && it.audio_status === "ready" && (!!it.audio_url || !!it.client_ritual);

export default function CareForSomeoneElsePage() {
  const nav = useNavigate();
  const [manifest, setManifest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [careCircle, setCareCircle] = useState({ one_person: "", one_place: "", one_thing: "" });
  const [savedAt, setSavedAt] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [m, cc] = await Promise.all([
          axios.get(`${API}/api/foundations/manifest`, auth()),
          axios.get(`${API}/api/foundations/care-circle`, auth()).catch(() => ({ data: null })),
        ]);
        if (cancelled) return;
        setManifest(m.data);
        if (cc?.data) {
          setCareCircle({
            one_person: cc.data.one_person || "",
            one_place: cc.data.one_place || "",
            one_thing: cc.data.one_thing || "",
          });
          setSavedAt(cc.data.updated_at || null);
        }
      } catch (_) {
        /* home will have shown any auth error */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const items = manifest?.items || [];
  const hasAccess = !!manifest?.has_access;
  const before = items.find((it) => it.slot === "before_i_help");
  const after = items.find((it) => it.slot === "after_being_there");

  const openAudio = (it) => {
    if (!hasAccess || !isReady(it)) return;
    nav(`/foundations/play/${it.id}`);
  };

  const saveCareCircle = async () => {
    setSaving(true);
    try {
      const r = await axios.put(
        `${API}/api/foundations/care-circle`,
        careCircle,
        auth(),
      );
      setSavedAt(r.data?.updated_at || new Date().toISOString());
    } catch (_) {
      /* UI feedback stays non-alarming; user can simply try again */
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: TOK.bg, color: TOK.creamDim }}
      >
        <p className="italic" style={{ fontFamily: SERIF }}>Preparing…</p>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen relative overflow-hidden"
      style={{ background: TOK.bg, color: TOK.cream }}
      data-testid="care-for-someone-else-page"
    >
      <div className="max-w-md mx-auto px-5 pt-8 pb-24">
        <div className="text-center">
          <SoulumoWord size="sm" />
          <div
            className="mt-2"
            style={{
              color: TOK.goldSoft, fontFamily: SANS, fontSize: "0.6rem",
              letterSpacing: "0.42em", textTransform: "uppercase",
            }}
          >
            Care for someone else
          </div>
        </div>

        <h1
          className="mt-10 text-center italic leading-tight"
          style={{
            color: TOK.cream, fontFamily: SERIF,
            fontSize: "clamp(1.5rem, 5.5vw, 1.9rem)", fontWeight: 300,
          }}
          data-testid="care-else-headline"
        >
          Being there for someone you love.
        </h1>
        <p
          className="mt-4 text-center max-w-sm mx-auto"
          style={{
            color: TOK.creamDim, fontFamily: SANS,
            fontSize: "0.95rem", lineHeight: 1.6,
          }}
        >
          {COMPASS.intro}
        </p>

        {/* 1 · LISTEN → UNDERSTAND → LOVE compass */}
        <section
          className="mt-10 rounded-2xl p-6"
          style={{ background: TOK.cardBg, border: `1px solid ${TOK.cardBorder}` }}
          data-testid="care-else-compass"
        >
          <div
            className="uppercase text-center"
            style={{
              color: TOK.goldSoft, fontFamily: SANS, fontSize: "0.62rem",
              letterSpacing: "0.4em",
            }}
          >
            A gentle compass
          </div>
          <h2
            className="mt-3 text-center italic"
            style={{
              color: TOK.cream, fontFamily: SERIF, fontSize: "1.3rem", fontWeight: 300,
            }}
          >
            {COMPASS.title}
          </h2>
          <ol className="mt-5 space-y-4">
            {COMPASS.steps.map((s) => (
              <li key={s.word}>
                <div
                  className="uppercase"
                  style={{
                    color: TOK.goldWarm, fontFamily: SANS,
                    fontSize: "0.72rem", letterSpacing: "0.26em",
                  }}
                >
                  {s.word}
                </div>
                <p
                  className="mt-1"
                  style={{
                    color: TOK.creamStrong, fontFamily: SANS,
                    fontSize: "0.92rem", lineHeight: 1.6,
                  }}
                >
                  {s.body}
                </p>
              </li>
            ))}
          </ol>
          <p
            className="mt-6 text-center italic"
            style={{
              color: TOK.goldSoft, fontFamily: SERIF,
              fontSize: "1rem", lineHeight: 1.5,
            }}
          >
            {COMPASS.anchor}
          </p>
        </section>

        {/* 2 · Before I Help */}
        <AudioCard
          title="Before I Help"
          duration="60–90 seconds"
          blurb="A gentle pause before you go to be with someone. You do not have to have the answer. Presence is enough."
          onPlay={() => openAudio(before)}
          disabled={!hasAccess || !isReady(before)}
          notReadyLabel={!isReady(before) ? "Arriving today" : null}
          testid="card-before-i-help"
        />

        {/* 3 · I Don't Know What to Say */}
        <section
          className="mt-6 rounded-2xl p-6"
          style={{ background: TOK.cardBg, border: `1px solid ${TOK.cardBorder}` }}
          data-testid="care-else-phrases"
        >
          <div
            className="uppercase"
            style={{
              color: TOK.goldSoft, fontFamily: SANS, fontSize: "0.62rem",
              letterSpacing: "0.4em",
            }}
          >
            I don't know what to say
          </div>
          <p
            className="mt-2 italic"
            style={{
              color: TOK.creamDim, fontFamily: SERIF, fontSize: "0.9rem",
            }}
          >
            Any one of these is enough.
          </p>
          <ul className="mt-4 space-y-3">
            {PHRASES_WHEN_STUCK.map((p, i) => (
              <li
                key={i}
                className="rounded-xl px-4 py-3"
                style={{
                  background: "rgba(224,169,122,0.07)",
                  border: `1px solid ${TOK.goldMist}`,
                  color: TOK.creamStrong, fontFamily: SERIF,
                  fontSize: "1rem", lineHeight: 1.5, fontStyle: "italic",
                }}
              >
                "{p}"
              </li>
            ))}
          </ul>
        </section>

        {/* 4 · Listen or Help? */}
        <section
          className="mt-6 rounded-2xl p-6"
          style={{ background: TOK.cardBg, border: `1px solid ${TOK.cardBorder}` }}
          data-testid="care-else-listen-or-help"
        >
          <div
            className="uppercase"
            style={{
              color: TOK.goldSoft, fontFamily: SANS, fontSize: "0.62rem",
              letterSpacing: "0.4em",
            }}
          >
            Listen or help?
          </div>
          <h3
            className="mt-3 italic"
            style={{
              color: TOK.cream, fontFamily: SERIF, fontSize: "1.3rem", fontWeight: 300,
            }}
          >
            {LISTEN_OR_HELP.principle}
          </h3>
          <p
            className="mt-3"
            style={{
              color: TOK.creamStrong, fontFamily: SANS,
              fontSize: "0.95rem", lineHeight: 1.6,
            }}
          >
            {LISTEN_OR_HELP.body}
          </p>
        </section>

        {/* 5 · After I've Been There for Someone */}
        <AudioCard
          title="After I've Been There for Someone"
          duration="2 minutes"
          blurb="A return to yourself after supporting someone. Breathe · feet · return · let go. Caring did not require you to carry it home."
          onPlay={() => openAudio(after)}
          disabled={!hasAccess || !isReady(after)}
          notReadyLabel={!isReady(after) ? "Arriving today" : null}
          testid="card-after-being-there"
        />

        {/* 6 · Loving Boundaries */}
        <section
          className="mt-6 rounded-2xl p-6"
          style={{ background: TOK.cardBg, border: `1px solid ${TOK.cardBorder}` }}
          data-testid="care-else-boundaries"
        >
          <div
            className="uppercase"
            style={{
              color: TOK.goldSoft, fontFamily: SANS, fontSize: "0.62rem",
              letterSpacing: "0.4em",
            }}
          >
            Loving boundaries
          </div>
          <p
            className="mt-3 italic"
            style={{
              color: TOK.cream, fontFamily: SERIF, fontSize: "1.1rem",
              lineHeight: 1.5,
            }}
          >
            {LOVING_BOUNDARIES.anchor}
          </p>
          <ul className="mt-4 space-y-1.5">
            {LOVING_BOUNDARIES.lines.map((l, i) => (
              <li
                key={i}
                style={{
                  color: TOK.creamStrong, fontFamily: SANS,
                  fontSize: "0.9rem", lineHeight: 1.6,
                }}
              >
                — {l}
              </li>
            ))}
          </ul>
          <p
            className="mt-5"
            style={{
              color: TOK.creamMist, fontFamily: SANS,
              fontSize: "0.82rem", lineHeight: 1.5,
            }}
          >
            Need to put the day down for a few minutes?{" "}
            <button
              type="button"
              onClick={() => nav("/foundations/care-for-me")}
              className="underline"
              style={{ color: TOK.goldSoft, background: "transparent", fontFamily: SANS }}
              data-testid="care-else-boundaries-link"
            >
              Open Care for me.
            </button>
          </p>
        </section>

        {/* 7 · When Love Means Getting More Help */}
        <section
          className="mt-6 rounded-2xl p-6"
          style={{
            background: "rgba(224,122,106,0.06)",
            border: "1px solid rgba(224,122,106,0.26)",
          }}
          data-testid="care-else-more-help"
        >
          <div
            className="uppercase"
            style={{
              color: "rgba(240,170,150,0.9)", fontFamily: SANS,
              fontSize: "0.62rem", letterSpacing: "0.4em",
            }}
          >
            When love means getting more help
          </div>
          <h3
            className="mt-3 italic"
            style={{
              color: TOK.cream, fontFamily: SERIF, fontSize: "1.15rem",
              fontWeight: 300, lineHeight: 1.4,
            }}
          >
            {WHEN_MORE_HELP.heading}
          </h3>
          <p
            className="mt-3"
            style={{
              color: TOK.creamStrong, fontFamily: SANS,
              fontSize: "0.95rem", lineHeight: 1.65,
            }}
          >
            {WHEN_MORE_HELP.body}
          </p>
        </section>

        {/* 8 · Your Care Circle */}
        <section
          className="mt-6 rounded-2xl p-6"
          style={{ background: TOK.cardBg, border: `1px solid ${TOK.cardBorder}` }}
          data-testid="care-circle"
        >
          <div
            className="uppercase"
            style={{
              color: TOK.goldSoft, fontFamily: SANS, fontSize: "0.62rem",
              letterSpacing: "0.4em",
            }}
          >
            Your Care Circle
          </div>
          <p
            className="mt-2 italic"
            style={{ color: TOK.creamDim, fontFamily: SERIF, fontSize: "0.92rem" }}
          >
            Who cares for the carer? A tiny reflection, just for you.
          </p>
          <div className="mt-4 space-y-4">
            <CareCircleField
              label="One person I can call"
              placeholder="A name — or just a first name"
              value={careCircle.one_person}
              onChange={(v) => setCareCircle((c) => ({ ...c, one_person: v }))}
              testid="care-circle-person"
            />
            <CareCircleField
              label="One place I can breathe"
              placeholder="A room, a chair, a bench, a view"
              value={careCircle.one_place}
              onChange={(v) => setCareCircle((c) => ({ ...c, one_place: v }))}
              testid="care-circle-place"
            />
            <CareCircleField
              label="One thing that brings me back to myself"
              placeholder="A song, a walk, a cup of tea, a prayer"
              value={careCircle.one_thing}
              onChange={(v) => setCareCircle((c) => ({ ...c, one_thing: v }))}
              testid="care-circle-thing"
            />
          </div>
          {hasAccess ? (
            <div className="mt-5 flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={saveCareCircle}
                disabled={saving}
                className="rounded-full uppercase"
                style={{
                  background: "linear-gradient(180deg, #f0c790 0%, #d99b6b 100%)",
                  border: "1px solid #f5e0c5", color: "#1a0f05",
                  fontFamily: SANS, fontSize: "0.68rem", fontWeight: 700,
                  letterSpacing: "0.22em", padding: "11px 20px",
                  minHeight: 44, opacity: saving ? 0.6 : 1,
                }}
                data-testid="care-circle-save"
              >
                {saving ? "Saving…" : "Save gently"}
              </button>
              {savedAt && (
                <span
                  className="italic"
                  style={{ color: TOK.creamFaint, fontFamily: SERIF, fontSize: "0.78rem" }}
                  data-testid="care-circle-saved-marker"
                >
                  Saved
                </span>
              )}
            </div>
          ) : (
            <p
              className="mt-5 italic"
              style={{ color: TOK.creamMist, fontFamily: SERIF, fontSize: "0.85rem" }}
            >
              Sign in to save this for yourself.
            </p>
          )}
        </section>

        <div className="mt-14 flex justify-center">
          <button
            onClick={() => nav("/foundations")}
            className="uppercase"
            style={{
              background: "transparent", color: TOK.creamMist, fontFamily: SANS,
              fontSize: "0.6rem", letterSpacing: "0.28em",
              padding: "12px 6px", borderBottom: `1px dashed ${TOK.goldMist}`,
              minHeight: 44,
            }}
            data-testid="care-else-back"
          >
            ← Back to Foundations
          </button>
        </div>
      </div>
    </div>
  );
}

function AudioCard({ title, duration, blurb, onPlay, disabled, notReadyLabel, testid }) {
  return (
    <section
      className="mt-6 rounded-2xl p-6"
      style={{ background: TOK.cardBg, border: `1px solid ${TOK.cardBorder}` }}
      data-testid={testid}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div
            className="uppercase"
            style={{
              color: TOK.goldSoft, fontFamily: SANS, fontSize: "0.62rem",
              letterSpacing: "0.4em",
            }}
          >
            {duration}
          </div>
          <h3
            className="mt-2 italic"
            style={{
              color: TOK.cream, fontFamily: SERIF, fontSize: "1.25rem",
              fontWeight: 300, lineHeight: 1.3,
            }}
          >
            {title}
          </h3>
        </div>
      </div>
      <p
        className="mt-3"
        style={{
          color: TOK.creamStrong, fontFamily: SANS,
          fontSize: "0.92rem", lineHeight: 1.6,
        }}
      >
        {blurb}
      </p>
      <div className="mt-5 flex items-center gap-3">
        <button
          type="button"
          onClick={onPlay}
          disabled={disabled}
          className="rounded-full uppercase"
          style={{
            background: disabled
              ? "rgba(245,236,217,0.08)"
              : "linear-gradient(180deg, #f0c790 0%, #d99b6b 100%)",
            border: disabled ? `1px solid ${TOK.cardBorder}` : "1px solid #f5e0c5",
            color: disabled ? TOK.creamMist : "#1a0f05",
            fontFamily: SANS, fontSize: "0.7rem", fontWeight: 700,
            letterSpacing: "0.22em", padding: "12px 22px",
            minHeight: 44, cursor: disabled ? "default" : "pointer",
          }}
          data-testid={`${testid}-play`}
        >
          {notReadyLabel || "Play"}
        </button>
      </div>
    </section>
  );
}

function CareCircleField({ label, placeholder, value, onChange, testid }) {
  return (
    <label className="block">
      <span
        className="block uppercase"
        style={{
          color: TOK.goldSoft, fontFamily: SANS, fontSize: "0.62rem",
          letterSpacing: "0.3em",
        }}
      >
        {label}
      </span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        maxLength={160}
        className="mt-2 w-full rounded-xl px-4 py-3"
        style={{
          background: "rgba(0,0,0,0.22)",
          border: `1px solid ${TOK.goldMist}`,
          color: TOK.cream, fontFamily: SERIF, fontSize: "1rem",
          outline: "none",
        }}
        data-testid={testid}
      />
    </label>
  );
}
